import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FiBox, FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { MoedaInput } from '../../components/MoedaInput';
import { Table } from '../../components/Table';
import { Card } from '../../components/Card';
import { AcaoIcone } from '../../components/AcaoIcone';
import {
  atualizarMateriaPrima,
  criarMateriaPrima,
  DadosMateriaPrima,
  listarMateriasPrimas,
  removerMateriaPrima,
} from '../../api/materiaPrima';
import { MateriaPrima } from '../../types';
import { formatarMoeda, formatarQuantidade } from '../../utils/formatadores';

const FORM_INICIAL: DadosMateriaPrima = { nome: '', unidade_medida: '', custo_unitario: '', estoque_atual: '' };

const UNIDADES_MEDIDA = [
  { value: 'kg', label: 'kg — quilograma' },
  { value: 'g', label: 'g — grama' },
  { value: 'l', label: 'l — litro' },
  { value: 'ml', label: 'ml — mililitro' },
  { value: 'un', label: 'un — unidade' },
  { value: 'dz', label: 'dz — dúzia' },
  { value: 'cx', label: 'cx — caixa' },
  { value: 'pct', label: 'pct — pacote' },
  { value: 'fatia', label: 'fatia' },
  { value: 'porcao', label: 'porção' },
];

export function MateriaPrimaLista() {
  const [itens, setItens] = useState<MateriaPrima[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<DadosMateriaPrima>(FORM_INICIAL);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    setCarregando(true);
    listarMateriasPrimas()
      .then(setItens)
      .finally(() => setCarregando(false));
  }

  useEffect(carregar, []);

  function handleChange(campo: keyof DadosMateriaPrima) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  }

  function iniciarEdicao(item: MateriaPrima) {
    setEditandoId(item.id);
    setForm({
      nome: item.nome,
      unidade_medida: item.unidade_medida,
      custo_unitario: Number(item.custo_unitario),
      estoque_atual: Number(item.estoque_atual),
    });
  }

  function cancelarEdicao() {
    setEditandoId(null);
    setForm(FORM_INICIAL);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    try {
      if (editandoId) {
        await atualizarMateriaPrima(editandoId, form);
      } else {
        await criarMateriaPrima(form);
      }
      cancelarEdicao();
      carregar();
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Não foi possível salvar',
        text: error.response?.data?.erro || 'Verifique os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemover(item: MateriaPrima) {
    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: `Remover "${item.nome}"?`,
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    await removerMateriaPrima(item.id);
    carregar();
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiBox size={18} />
          </span>
          Matéria-prima
        </h1>
      </div>

      <Card style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '15px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiPlus size={16} style={{ color: 'var(--cor-primaria)' }} />
          {editandoId ? 'Editar matéria-prima' : 'Nova matéria-prima'}
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.3fr 1fr 1fr auto',
            gap: '12px',
            alignItems: 'end',
          }}
        >
          <Input label="Nome" value={form.nome} onChange={handleChange('nome')} required />
          <Select
            label="Unidade de medida"
            value={form.unidade_medida}
            onChange={handleChange('unidade_medida')}
            options={UNIDADES_MEDIDA}
            placeholder="Selecione"
            required
          />
          <MoedaInput
            label="Custo unitário"
            value={form.custo_unitario}
            onValueChange={(valor) => setForm((atual) => ({ ...atual, custo_unitario: valor }))}
          />
          <Input
            label="Estoque atual"
            type="number"
            step="0.0001"
            min="0"
            value={form.estoque_atual}
            onChange={handleChange('estoque_atual')}
          />
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Button type="submit" disabled={salvando} fullWidth={false}>
              {editandoId ? 'Salvar' : 'Adicionar'}
            </Button>
            {editandoId && (
              <Button type="button" variant="outline" onClick={cancelarEdicao} fullWidth={false}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Card>

      {carregando ? (
        <p>Carregando...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Unidade</th>
              <th>Custo unitário</th>
              <th>Estoque</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id}>
                <td>{item.nome}</td>
                <td>{UNIDADES_MEDIDA.find((u) => u.value === item.unidade_medida)?.label ?? item.unidade_medida}</td>
                <td>{formatarMoeda(item.custo_unitario)}</td>
                <td>
                  {formatarQuantidade(item.estoque_atual)} {item.unidade_medida}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <AcaoIcone icon={<FiEdit2 size={15} />} onClick={() => iniciarEdicao(item)} aria-label="Editar" />
                    <AcaoIcone icon={<FiTrash2 size={15} />} tone="perigo" onClick={() => handleRemover(item)} aria-label="Remover" />
                  </div>
                </td>
              </tr>
            ))}
            {itens.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '16px', textAlign: 'center' }}>
                  Nenhuma matéria-prima cadastrada ainda.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </AppLayout>
  );
}
